import { api, type Page } from "@/lib/api/client";
import type {
  CostCenter,
  NumberSequence,
  JobTitle,
  LegalEntity,
  OrgUnit,
  Position,
  ScopedListFilters,
  Site,
} from "./admin.types";

/**
 * The six Kuruluş resources speak one dialect.
 *
 * Every one of them lists on `page/limit/q/sortBy/sortOrder/status`, creates with a body,
 * patches with a body carrying `version`, soft-deletes, and restores. Written out six times
 * this file would be six hundred lines in which one path is a typo and nobody notices until a
 * screen 404s. The factory is the shape; a resource is a base path and its types.
 */
function resource<TRow, TCreate, TUpdate>(base: string) {
  return {
    list(filters: ScopedListFilters): Promise<Page<TRow>> {
      return api.page<TRow>(base, { params: { ...filters } });
    },
    get(id: string): Promise<TRow> {
      return api.get<TRow>(`${base}/${id}`);
    },
    create(body: TCreate): Promise<TRow> {
      return api.post<TRow>(base, body);
    },
    /** `version` travels with every patch — the server answers 409 `STALE_VERSION` on a stale one. */
    update(id: string, body: TUpdate & { version: number }): Promise<TRow> {
      return api.patch<TRow>(`${base}/${id}`, body);
    },
    /** Soft delete: the row keeps its history and can be restored. */
    remove(id: string): Promise<void> {
      return api.delete(`${base}/${id}`);
    },
    restore(id: string): Promise<TRow> {
      return api.post<TRow>(`${base}/${id}/restore`);
    },
  };
}

export interface LegalEntityInput {
  code: string;
  legalName: string;
  tradeName?: string | undefined;
  taxOffice?: string | undefined;
  taxNumber?: string | undefined;
  baseCurrency?: string | undefined;
  timezone?: string | undefined;
  status?: string | undefined;
}

export interface SiteInput {
  legalEntityId: string;
  code: string;
  name: string;
  siteType?: string | undefined;
  timezone?: string | undefined;
  status?: string | undefined;
}

export interface OrgUnitInput {
  legalEntityId: string;
  parentId?: string | undefined;
  siteId?: string | undefined;
  unitType?: string | undefined;
  code: string;
  name: string;
  status?: string | undefined;
}

export interface JobTitleInput {
  code: string;
  name: string;
  description?: string | undefined;
  status?: string | undefined;
}

export interface PositionInput {
  legalEntityId: string;
  organizationUnitId: string;
  jobTitleId: string;
  siteId?: string | undefined;
  reportsToPositionId?: string | undefined;
  code: string;
  headcount?: number | undefined;
  status?: string | undefined;
}

export interface CostCenterInput {
  legalEntityId: string;
  code: string;
  name: string;
  status?: string | undefined;
}

export const organizationApi = {
  legalEntities: resource<LegalEntity, LegalEntityInput, Partial<LegalEntityInput>>(
    "/organization/legal-entities",
  ),
  sites: resource<Site, SiteInput, Partial<SiteInput>>("/organization/sites"),
  orgUnits: resource<OrgUnit, OrgUnitInput, Partial<OrgUnitInput>>("/organization/org-units"),
  jobTitles: resource<JobTitle, JobTitleInput, Partial<JobTitleInput>>("/organization/job-titles"),
  positions: resource<Position, PositionInput, Partial<PositionInput>>("/organization/positions"),
  costCenters: resource<CostCenter, CostCenterInput, Partial<CostCenterInput>>(
    "/organization/cost-centers",
  ),
  /**
   * `resource<>` KULLANILMIYOR ve sebebi tek bir sütun: o yardımcı her `update`'e
   * `version` dayatıyor, `number_sequences` tablosunda ise iyimser kilit sütunu yok — ve
   * olmasına gerek de yok, çünkü aynı anda iki kişi düzenlese bile kaybedilecek iş yok:
   * sayacın geri gitmesini veritabanı zaten reddediyor.
   *
   * `create` ve `remove` de yok, sunucuda da yok: seri ilk belgede kendiliğinden doğuyor,
   * silinmesi ise sayacı sıfırlamak olurdu.
   */
  numberSequences: {
    list: (filters: ScopedListFilters) =>
      api.page<NumberSequence>("/organization/number-sequences", { params: { ...filters } }),
    update: (id: string, body: { prefix?: string; padding?: number; nextValue?: number }) =>
      api.patch<NumberSequence>(`/organization/number-sequences/${id}`, body),
  },
};

export interface UnitManager {
  id: string;
  organizationUnitId: string;
  employmentId: string;
  employment: { id: string; employeeNo: string; fullName: string } | null;
  isPrimary: boolean;
  validFrom: string | null;
  validTo: string | null;
  /** Bugün yürürlükte mi — bitiş günü DAHİL, onay motoruyla aynı okuma. */
  active: boolean;
  /**
   * İptal edilmiş atama: bitişi başlangıcından ÖNCE (aynı gün atanıp değiştirilen ya da başlamadan geri
   * çekilen). Hiçbir günü yönetmedi; geçmişte «iptal edildi» diye anılır.
   */
  voided: boolean;
}

/**
 * Who heads a unit, and since when.
 *
 * Dated rather than a plain column, and that is the whole point: the approval chain asks "who
 * led this unit ON the day the request was filed", not "who leads it now". A leaver's decisions
 * have to stay explicable after they have gone.
 */
export const unitManagers = {
  /**
   * `includeEnded`: bitmiş, iptal edilmiş ve henüz başlamamış atamalar da gelir (geçmiş + planlanan).
   * Varsayılan yalnız bugün yürürlükte olanlar — ve Birim başı kutusunun «Geçmiş görevler» bölümü bu
   * yüzden hep boştu (20 Ekim 2026'ya kadar).
   */
  list(unitId: string, options: { includeEnded?: boolean } = {}): Promise<UnitManager[]> {
    return api.get<UnitManager[]>(`/organization/org-units/${unitId}/managers`, {
      ...(options.includeEnded ? { params: { includeEnded: true } } : {}),
    });
  },
  add(
    unitId: string,
    body: { employmentId: string; isPrimary?: boolean | undefined; validFrom: string },
  ): Promise<UnitManager> {
    return api.post<UnitManager>(`/organization/org-units/${unitId}/managers`, body);
  },
  /**
   * Ends the assignment rather than deleting it — see the note above.
   *
   * `validTo` ZORUNLU (21 Ekim 2026): sunucu gövdede bitiş günü istiyor (`endUnitManagerSchema`) ve bu çağrı
   * eskiden gövdesiz gidiyordu — «Görevi bitir» her seferinde 400 «Bu alan zorunlu» alıyordu. Bitiş günü
   * dahil okunduğu için çağıran DÜNÜ verir; bugün başlamış bir atama için sunucu bunu iptale çevirir.
   */
  end(unitId: string, managerId: string, validTo: string): Promise<unknown> {
    return api.post(`/organization/org-units/${unitId}/managers/${managerId}/end`, { validTo });
  },
};

/** Moving a unit is its own endpoint: the server rewrites the subtree's paths and depths. */
export function moveOrgUnit(id: string, parentId: string, version: number): Promise<OrgUnit> {
  return api.post<OrgUnit>(`/organization/org-units/${id}/move`, { parentId, version });
}
